import { infiniteQueryOptions, mutationOptions, queryOptions } from '@tanstack/react-query';
import { sparkApi } from '../spark-client';

export interface Conversation {
  id: string;
  title: string;
  conversation_type: ConversationType;
  created_at: string;
  participant_names: string[];
  owner_name: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender: { user_id: string | null; contact_id: string | null };
  content: string;
  created_at: string;
  read_at: string | null;
}

export enum ContactType {
  SMS = 'sms',
  WHATSAPP = 'whatsapp',
}

export interface Contact {
  id: string;
  owner_user_id: string;
  linked_user_id: string | null;
  name: string;
  phone_number: string | null;
  email: string | null;
  contact_type: ContactType;
  created_at: string;
  updated_at: string;
}

interface CreateConversationResponseDto {
  created: boolean;
  existing_conversation_id_with_same_participants: string | null;
}

export const userHumanToHumanConversationsQueryOptions = () =>
  queryOptions({
    queryKey: ['conversations', ConversationType.HUMAN_TO_HUMAN],
    queryFn: async (): Promise<Conversation[]> => {
      const response = await sparkApi.get('/api/v1/conversations', {
        params: {
          conversation_type: ConversationType.HUMAN_TO_HUMAN,
        },
      });
      return response.data;
    },
  });

export const conversationMessagesQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: ['conversations', ConversationType.HUMAN_TO_HUMAN, conversationId, 'messages'],
    queryFn: async (): Promise<{
      conversation: Conversation;
      conversation_messages: Message[];
    }> => {
      const response = await sparkApi.get(`/api/v1/conversations/${conversationId}/messages`);
      return response.data;
    },
    enabled: !!conversationId,
  });

const MESSAGE_PAGE_SIZE = 20;

export const conversationMessagesInfiniteQueryOptions = (
  conversationId: string,
  enabled: boolean = true
) =>
  infiniteQueryOptions({
    queryKey: [
      'conversations',
      ConversationType.HUMAN_TO_HUMAN,
      conversationId,
      'messages',
      'infinite',
    ],
    enabled: enabled && !!conversationId,
    queryFn: async ({ signal, pageParam }) => {
      const params = new URLSearchParams({
        limit: MESSAGE_PAGE_SIZE.toString(),
        ...(pageParam ? { before_id: pageParam as string } : {}),
      });

      const response = await sparkApi.get(
        `/api/v1/conversations/${conversationId}/messages?${params}`,
        { signal }
      );

      return {
        conversation: response.data.conversation,
        messages: response.data.conversation_messages,
        hasMore: response.data.has_more || false,
        oldestMessageId: response.data.oldest_message_id,
      };
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      return lastPage.hasMore ? lastPage.oldestMessageId : undefined;
    },
    staleTime: 1000 * 60 * 5,
  });

export const contactsQueryOptions = () =>
  queryOptions({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await sparkApi.get<Contact[]>('/api/v1/contacts/');
      return response.data;
    },
  });

export const sendMessageMutationOptions = () =>
  mutationOptions({
    mutationFn: async ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: string;
    }) => {
      await sparkApi.post(`/api/v1/conversations/${conversationId}/messages/send`, { content });
    },
  });

export const createContactMutationOptions = () =>
  mutationOptions({
    mutationFn: async (
      contactData: Omit<Contact, 'id' | 'owner_user_id' | 'created_at' | 'updated_at'>
    ) => {
      const response = await sparkApi.post<Contact>('/api/v1/contacts/', contactData);
      return response.data;
    },
  });

export const updateContactMutationOptions = () =>
  mutationOptions({
    mutationFn: async ({ id, contactData }: { id: string; contactData: Partial<Contact> }) => {
      const response = await sparkApi.put<Contact>(`/api/v1/contacts/${id}`, contactData);
      return response.data;
    },
  });

export const deleteContactMutationOptions = () =>
  mutationOptions({
    mutationFn: async (contactId: string) => {
      await sparkApi.delete(`/api/v1/contacts/${contactId}`);
    },
  });

export enum ConversationType {
  HUMAN_TO_HUMAN = 'human_to_human',
  HUMAN_TO_AGENT = 'human_to_agent',
}

export const createConversationMutationOptions = () =>
  mutationOptions({
    mutationFn: async ({
      conversationId,
      conversationType,
      conversationTitle,
      recipientContactIds,
    }: {
      conversationId: string;
      conversationType: ConversationType;
      conversationTitle?: string;
      recipientContactIds: string[];
    }) => {
      const response = await sparkApi.post<CreateConversationResponseDto>('/api/v1/conversations', {
        conversationId,
        conversationType,
        conversationTitle,
        recipientContactIds,
      });
      return response.data;
    },
  });
